import baseApi from 'shared/api/baseApi';

import type { DemoChatMessage } from '../model/message';

interface CreateDemoChatClientArgs {
  companyIdentifier: string;
}

interface CreateDemoChatClientResponse {
  id: string;
}

interface GetDemoChatMessagesArgs {
  clientPhoneIdentifier: string;
}

interface CreateDemoChatMessageArgs {
  clientPhoneIdentifier: string;
  message?: string;
  file?: File;
}

interface CreateDemoChatMessageResponse {
  status: string;
}

export const demoChatApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    createDemoChatClient: build.mutation<CreateDemoChatClientResponse, CreateDemoChatClientArgs>({
      query: ({ companyIdentifier }) => ({
        url: '/demoChat/clients',
        method: 'POST',
        body: { companyIdentifier },
      }),
      invalidatesTags: [{ type: 'DemoChatMessages', id: 'LIST' }],
    }),
    getDemoChatMessages: build.query<DemoChatMessage[], GetDemoChatMessagesArgs>({
      query: ({ clientPhoneIdentifier }) => ({
        url: `/demoChat/messages?clientPhoneIdentifier=${encodeURIComponent(clientPhoneIdentifier)}`,
        method: 'GET',
      }),
      providesTags: (result, _error, { clientPhoneIdentifier }) => {
        const listTag = { type: 'DemoChatMessages' as const, id: `LIST-${clientPhoneIdentifier}` };
        if (!result) return [listTag];
        return [...result.map(({ id }) => ({ type: 'DemoChatMessages' as const, id })), listTag];
      },
    }),
    createDemoChatMessage: build.mutation<CreateDemoChatMessageResponse, CreateDemoChatMessageArgs>({
      query: ({ clientPhoneIdentifier, message, file }) => {
        if (file) {
          const form = new FormData();
          form.append('clientPhoneIdentifier', clientPhoneIdentifier);
          if (message) form.append('message', message);
          form.append('file', file);
          return {
            url: '/demoChat/messages',
            method: 'POST',
            body: form,
          };
        }
        return {
          url: '/demoChat/messages',
          method: 'POST',
          body: { clientPhoneIdentifier, message: message ?? '' },
          headers: { 'Content-Type': 'application/json' },
        };
      },
      invalidatesTags: (_result, _error, { clientPhoneIdentifier }) => [
        { type: 'DemoChatMessages', id: `LIST-${clientPhoneIdentifier}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useCreateDemoChatClientMutation, useGetDemoChatMessagesQuery, useCreateDemoChatMessageMutation } =
  demoChatApi;
